import React from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { GridRowId } from '@mui/x-data-grid';
import { Theme } from '@mui/material/styles';
import { getProjectInfo } from './project.api';

type SelectedProjectChipsProps = {
  selectedIds: readonly GridRowId[];
  onRemove?: (id: string | number) => void;
  showArrows?: boolean;
  allowDelete?: boolean;
  resolvedDetails?: Record<string, { number: string; title: string }>;
  autoFetch?: boolean;
};

const SelectedProjectChips: React.FC<SelectedProjectChipsProps> = ({
  selectedIds,
  onRemove,
  showArrows = true,
  allowDelete = true,
  resolvedDetails,
  autoFetch = false,
}) => {
  const chipsScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [details, setDetails] = React.useState<Record<string, { number: string; title: string }>>({});

  React.useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const map: Record<string, { number: string; title: string }> = { ...details };
      if (resolvedDetails) {
        Object.entries(resolvedDetails).forEach(([idStr, d]) => {
          map[idStr] = d;
        });
      }
      const toFetch = autoFetch ? selectedIds.map(String).filter(idStr => !map[idStr]) : [];
      if (toFetch.length > 0) {
        const fetched = await Promise.all(
          toFetch.map(async idStr => {
            try {
              const info = await getProjectInfo(idStr);
              return [idStr, { number: info.projectNumber || 'Unknown Number', title: info.projectTitle || 'Unknown Title' }] as const;
            } catch (_e) {
              return [idStr, { number: 'Unknown Number', title: 'Unknown Title' }] as const;
            }
          }),
        );
        fetched.forEach(([idStr, d]) => (map[idStr] = d));
      }
      if (!cancelled) setDetails(map);
    };
    if (selectedIds.length > 0) sync();
    return () => {
      cancelled = true;
    };
  }, [selectedIds, resolvedDetails, autoFetch]);

  React.useEffect(() => {
    const container = chipsScrollRef.current;
    if (!container) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const update = () => {
      const hasOverflow = container.scrollWidth > container.clientWidth + 1;
      const atStart = container.scrollLeft <= 1;
      const atEnd = container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;
      setCanScrollLeft(hasOverflow && !atStart);
      setCanScrollRight(hasOverflow && !atEnd);
    };
    update();
    const onScroll = () => update();
    const onResize = () => update();
    container.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [selectedIds, details]);

  const scrollRight = () => chipsScrollRef.current?.scrollBy({ left: 240, behavior: 'smooth' });
  const scrollLeft = () => chipsScrollRef.current?.scrollBy({ left: -240, behavior: 'smooth' });

  return (
    <Box display='flex' alignItems='flex-start' gap={1} width='100%'>
      {showArrows && canScrollLeft ? (
        <IconButton
          size='small'
          onClick={scrollLeft}
          aria-label='scroll left'
          sx={{
            boxShadow: 1,
            backgroundColor: '#f5f5f5',
            border: '1px solid #cccccc',
            color: 'text.secondary',
            borderRadius: '999px',
            '&:hover': { backgroundColor: '#e0e0e0' },
          }}
        >
          <ArrowBackIosNewIcon fontSize='small' />
        </IconButton>
      ) : null}

      <Box
        ref={chipsScrollRef}
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 1,
          alignItems: 'center',
          overflowX: 'auto',
          overflowY: 'visible',
          whiteSpace: 'nowrap',
          px: 0.3,
          py: 0.3,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          flexGrow: 1,
        }}
      >
        {selectedIds.map((id, index) => {
          const idStr = String(id);
          const d = resolvedDetails?.[idStr] || details[idStr];
          const isReady = Boolean(d);
          if (!isReady && !autoFetch) {
            return null;
          }
          const label = isReady ? `${(d as any).number} - ${(d as any).title}` : '';
          return (
            <Chip
              key={idStr}
              label={label}
              onDelete={isReady && allowDelete && onRemove ? () => onRemove(idStr) : undefined}
              variant='outlined'
              size='medium'
              sx={{
                backgroundColor: '#f5f5f5',
                borderColor: '#cccccc',
                color: 'text.primary',
                flexShrink: 0,
                opacity: isReady ? 1 : 0.85,
                transition: 'all 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transform: 'scale(1)',
                animationDelay: `${index * 50}ms`,
                '& .MuiChip-label': {
                  maxWidth: 330,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(0,0,0,0.38)',
                  transition: 'color 150ms ease',
                },
                '&:hover .MuiChip-deleteIcon': {
                  color: (theme: Theme) => theme.palette.error.main,
                },
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                  transform: 'translateY(-1.5px) scale(1.02)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                },
                '@keyframes chipAppear': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.8) translateY(10px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1) translateY(0)',
                  },
                },
                animation: 'chipAppear 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            />
          );
        })}
      </Box>

      {showArrows && canScrollRight ? (
        <IconButton
          size='small'
          onClick={scrollRight}
          aria-label='scroll right'
          sx={{
            boxShadow: 1,
            backgroundColor: '#f5f5f5',
            border: '1px solid #cccccc',
            color: 'text.secondary',
            borderRadius: '999px',
            '&:hover': { backgroundColor: '#e0e0e0' },
          }}
        >
          <ArrowForwardIosIcon fontSize='small' />
        </IconButton>
      ) : null}
    </Box>
  );
};

export default SelectedProjectChips;
